import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetMolecularWeightQuery as GetMolecularWeightQueryResponse,
  GetMolecularWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetMolecularWeightQuery';
import {GET_MOLECULAR_WEIGHT_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {
  CustomResolutionQueryType,
  GetMolecularWeightQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export async function bulkResolveGetMolecularWeightToServerResolutionResult(
  queries: Array<GetMolecularWeightQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<
    GetMolecularWeightQueryResponse,
    GetMolecularWeightQueryVariables
  >({query: GET_MOLECULAR_WEIGHT_QUERY}, {variables: {moleculeIds}});

  // Build a map of moleculeId -> molecular weight for efficient lookup
  const molecularWeightByMoleculeId = new Map<string, number | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      molecularWeightByMoleculeId.set(molecule.invalidId, null);
    } else {
      const molecularWeight = molecule.currentVersion.chemicalStructure.molecularWeight;
      if (molecularWeight.__typename === 'MolecularWeightNode') {
        molecularWeightByMoleculeId.set(molecule.id, molecularWeight.value);
      } else {
        molecularWeightByMoleculeId.set(molecule.id, null);
      }
    }
  }

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const molecularWeight = molecularWeightByMoleculeId.get(query.moleculeId);
    if (molecularWeight == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve molecular weight');
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.FLOAT,
      value: String(molecularWeight),
      rawValue: String(molecularWeight),
      floatValue: molecularWeight,
    });
  });
}

export function getMolecularWeightColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // If a user manually enters a molecular weight, don't make a resolution query
  // and validate it's a valid number
  if (!isTypedValueEmpty(typedValue)) {
    return {val: typedValue, error: null, locked: false};
  }

  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];

  // If there's no bioentity in the dependency, return empty with no error
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }

  const moleculeId = bioentityTypedValue.bioentitySummary.id;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULAR_WEIGHT,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
